import type {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';

export interface DecimalStringMaxValidatorOptions {
    max: string;
    scale: number;
}

/**
 * Validates a decimal input against a max boundary using BigInt math.
 *
 * This avoids floating-point precision issues for large decimal limits
 * (for example DECIMAL(18,2) values).
 */
export function decimalStringMaxValidator(
    options: DecimalStringMaxValidatorOptions
): ValidatorFn {
    const { max, scale } = options;
    const normalizedMax = toScaledBigInt(max, scale);
    if (normalizedMax == null) {
        throw new Error(
            `Invalid decimal max constant "${max}" for validator setup`
        );
    }

    return (control: AbstractControl<unknown>): ValidationErrors | null => {
        const value = control.value;
        if (value == null || value === '') return null;

        const normalizedValue = toScaledBigInt(String(value), scale);
        if (normalizedValue == null) return null;

        return normalizedValue <= normalizedMax
            ? null
            : {
                  max: {
                      max,
                      actual: value,
                  },
              };
    };
}

function toScaledBigInt(value: string, scale: number): bigint | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const normalizedValue = normalizeTrailingDecimalPoint(trimmed);
    if (!normalizedValue) return null;

    const match = /^([+-]?)(\d*)(?:\.(\d+))?$/.exec(normalizedValue);
    if (!match) return null;

    const [, sign, integerRaw, fractionRaw = ''] = match;
    if (!integerRaw && !fractionRaw) return null;
    if (fractionRaw.length > scale) return null;

    const integerPart = integerRaw || '0';
    const fractionPart = fractionRaw.padEnd(scale, '0');
    const magnitude = BigInt(`${integerPart}${fractionPart}`);

    return sign === '-' ? -magnitude : magnitude;
}

function normalizeTrailingDecimalPoint(value: string): string {
    return value.endsWith('.') ? value.slice(0, -1) : value;
}
